import { memo, useLayoutEffect, useRef } from 'react';
import autosize from 'autosize';

import { TextArea } from './Input.styled';

export interface InputProps {
  value: string;
  placeholder: string;
  onChange: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onSend: () => void;
  focus?: boolean;
  disabled?: boolean;
  maxLength?: number;
}

export const Input = memo<InputProps>((props) => {
  const { value, placeholder, onChange, onSend, disabled, focus, maxLength } = props;

  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  useLayoutEffect(() => {
    const ref = inputRef.current;
    if (!ref) {
      return;
    }

    autosize(ref as HTMLTextAreaElement);
    return () => {
      autosize.destroy(ref as Element);
    };
  }, []);

  useLayoutEffect(() => {
    autosize.update(inputRef.current as Element);
  }, [value]);

  useLayoutEffect(() => {
    if (focus && !disabled) {
      inputRef.current?.focus();
    }
  }, [focus, disabled]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      onSend();
    }
  };

  return (
    <TextArea
      value={value}
      data-testid="Input-wrapper"
      ref={inputRef}
      disabled={disabled}
      onChange={onChange}
      maxLength={maxLength}
      placeholder={placeholder}
      onKeyDown={handleKeyDown}
      rows={1}
    />
  );
});

Input.displayName = 'Input';
